import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Onboarding from "./components/Onboarding";
import Dashboard from "./components/Dashboard";
import BrainDump from "./components/BrainDump";
import GentleReview from "./components/GentleReview";
import Settings from "./components/Settings";
import Resources from "./components/Resources";
import WeeklyPlanner from "./components/WeeklyPlanner";
import MealCalendar from "./components/MealCalendar";
import ShoppingList from "./components/ShoppingList";
import SavedRecipes from "./components/SavedRecipes";
import PaymentSuccess from "./components/PaymentSuccess";
import WeeklyReset from "./components/WeeklyReset";
import JournalCalendar from "./components/JournalCalendar";
import Layout from "./components/Layout";
import InstallPrompt from "./components/InstallPrompt";
import { hasLifetimeAccess } from "./utils/localStorage";

// Premium route wrapper (requires lifetime access for ALL features)
const PremiumRoute = ({ children }) => {
  const [isPremium, setIsPremium] = useState(null);
  
  useEffect(() => {
    setIsPremium(hasLifetimeAccess());
  }, []);
  
  if (isPremium === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-sage animate-pulse" />
      </div>
    );
  }
  
  if (!isPremium) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Onboarding route wrapper - shows onboarding if not paid
const OnboardingRoute = ({ children }) => {
  const [isPremium, setIsPremium] = useState(null);
  
  useEffect(() => {
    setIsPremium(hasLifetimeAccess());
  }, []);
  
  if (isPremium === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-sage animate-pulse" />
      </div>
    );
  }
  
  // If already paid, redirect to dashboard
  if (isPremium) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Onboarding - only shows if not paid */}
          <Route 
            path="/" 
            element={
              <OnboardingRoute>
                <Onboarding />
              </OnboardingRoute>
            } 
          />
          
          {/* Payment Success - public route */}
          <Route 
            path="/payment-success" 
            element={<PaymentSuccess />} 
          />
          
          {/* All routes require lifetime access */}
          <Route 
            path="/dashboard" 
            element={
              <PremiumRoute>
                <Dashboard />
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/brain-dump" 
            element={
              <PremiumRoute>
                <Layout>
                  <BrainDump />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/review" 
            element={
              <PremiumRoute>
                <Layout>
                  <GentleReview />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/weekly-reset" 
            element={
              <PremiumRoute>
                <Layout>
                  <WeeklyReset />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/journal-calendar" 
            element={
              <PremiumRoute>
                <Layout>
                  <JournalCalendar />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/settings" 
            element={
              <PremiumRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/resources" 
            element={
              <PremiumRoute>
                <Layout>
                  <Resources />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/planner" 
            element={
              <PremiumRoute>
                <Layout>
                  <WeeklyPlanner />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/meals" 
            element={
              <PremiumRoute>
                <Layout>
                  <MealCalendar />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/shopping" 
            element={
              <PremiumRoute>
                <Layout>
                  <ShoppingList />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          <Route 
            path="/saved-recipes" 
            element={
              <PremiumRoute>
                <Layout>
                  <SavedRecipes />
                </Layout>
              </PremiumRoute>
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#FDFCF8',
            border: '1px solid #E8E4DE',
            color: '#4A4A4A',
            fontFamily: 'Nunito, sans-serif',
          },
        }}
      />
      
      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
}

export default App;
